import { useAuth0 } from "@auth0/auth0-react";
import Business from "@mui/icons-material/Business";
import { Alert, Box, Button, CssBaseline, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { HeaderBar } from "../../modules/generic/components/header-bar";
import { RouteTabs } from "../../modules/generic/components/route-tabs";
import { Page } from "../../modules/layout/components/page";
import { useLogo } from "../../modules/layout/components/sidebar/hooks/use-logo";
import { ThemeProvider } from "../../styles/theme/theme-provider";
import { ReviewsTable } from "./components/reviews-table";
import { useCancelledReviews } from "./hooks/use-cancelled-reviews";

import type { FC } from "react";

const title = "Approvals | ChangeEngine";
const base = "/reviewer/";

const DefaultPage: FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    void navigate(`${base}in-progress`, { replace: true });
  });
  return null;
};

/**
 * A reviewer's whole app. They have no permissions beyond their own reviews.
 *
 * It sits outside every layout group, so it brings its own theme. Light, because a reviewer never
 * reaches the settings menu that would toggle it.
 */
const ReviewerHomePage: FC = () => {
  const { user } = useAuth0();
  const { data } = useLogo();
  const { cancelled, dismiss } = useCancelledReviews();

  return (
    <ThemeProvider mode="light">
      <CssBaseline />
      <Page title={title}>
        <HeaderBar>
          <Stack alignItems="center" direction="row" spacing={1}>
            <Box sx={{ height: 35, width: 35, flexShrink: 0 }}>
              {data?.logo ? (
                <img
                  alt="logo"
                  src={data.logo}
                  style={{ objectFit: "contain", height: "100%", width: "100%" }}
                />
              ) : (
                <Business />
              )}
            </Box>
            <Typography variant="body1" component="h1" sx={{ fontWeight: "bold" }}>
              Approvals
            </Typography>
          </Stack>
          <Stack alignItems="center" direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
            <Button
              size="small"
              variant="text"
              data-analytics-id="reviewer-home-logout"
              component={Link}
              to="/logout"
            >
              Log out
            </Button>
          </Stack>
        </HeaderBar>
        {/* Display cancelled review notifications */}
        {cancelled.map(({ moment_id, title: cancelledTitle, cancelledBy }) => (
          <Alert
            key={moment_id}
            severity="error"
            onClose={() => void dismiss(moment_id)}
            sx={{ mx: "var(--page-px)", mt: 3 }}
          >
            {cancelledBy} cancelled the request for approval for “{cancelledTitle}” - you no longer
            have access to it.
          </Alert>
        ))}
        <RouteTabs
          defaultPage={<DefaultPage />}
          base={base}
          preserveQuery
          tabs={[
            {
              type: "explicit",
              data: {
                name: "In progress",
                route: "in-progress",
                analyticsId: "reviewer-home-tab-in-progress",
                queryParamsToRemove: ["status"],
              },
              component: <ReviewsTable variant="in-progress" />,
            },
            {
              type: "explicit",
              data: {
                name: "Previous requests",
                route: "previous-reviews",
                analyticsId: "reviewer-home-tab-previous-reviews",
                queryParamsToRemove: ["status"],
              },
              component: <ReviewsTable variant="previous" />,
            },
          ]}
          title={title}
        />
      </Page>
    </ThemeProvider>
  );
};

export default ReviewerHomePage;
